/**
 * Chart component map keyed by chart id. Replaces the single large switch in useAnalyticsChartData.
 * Each entry is self-contained; adding a new chart only requires adding one entry here and in the registry.
 */

import React from 'react';
import {
  ChartEmptyState,
  toActivitiesUrl,
  toAnalyticsUrl,
  getTopN,
  rankedBarTopN,
} from '@/components/visuals';
import type { ChartDensity, ScheduleDateBasisValue, UnitsSpreadBasisValue } from '@/components/visuals';
import type { ScheduleActivity } from '@/types';
import { buildTimePhasedUnitsSeries } from '@/utils/buildTimePhasedUnitsSeries';
import type { UnitsSpreadBasis } from '@/utils/resolvePlannedUnitsSeries';
import { resolvePlannedUnitsSeries } from '@/utils/resolvePlannedUnitsSeries';
import {
  FloatDistributionChart,
  CriticalityChart,
  MilestoneVarianceChart,
  WBSHotspotsChart,
  QualityChart,
  SCurveActivitiesChart,
  SCurveDurationChart,
  PlannedUnitsHistogramPanel,
  PlannedUnitsSCurveChart,
  SCurveWBSChart,
  MilestoneOnTimeChart,
  ResourceDemandCapacityChart,
  CriticalPathLengthChart,
  FloatTrendChart,
  NearCriticalTrendChart,
  LogicIntegrityTrendChart,
  PhaseProgressBurnupChart,
} from './charts';
import { BUCKET_TO_FLOAT_RANGE } from './helpers';
import type { ScheduleAnalyticsShapes, TrendsPoint } from '@/types/analyticsApi';
import { aggregateAverageUnitsPerDayByCalendarMonth, downsampleAlignedSeries, sampleCumulativeByCalendarMonth } from './previewAggregation';
import { UnitsPerDayHistogramChart } from './charts/UnitsPerDayHistogramChart';

// ---------------------------------------------------------------------------
// ChartRenderProps — data and options passed to every chart renderer
// ---------------------------------------------------------------------------

export interface ChartRenderProps {
  height: number | string;
  density: ChartDensity;
  showDataLabels: boolean;
  navigate: (url: string) => void;
  selectedProjectId: string | undefined;
  /** Focus override: show baseline series on S-curves when baseline data present */
  showBaseline?: boolean;
  /** Baseline schedule analytics (when showBaseline and baseline snapshot fetched) */
  baselineScheduleAnalytics?: ScheduleAnalyticsShapes | null;
  /** Focus override: limit trend/snapshot charts to last N points (slice from end) */
  maxPoints?: number;
  /** Float distribution buckets (from merged) */
  floatDist: Array<{ bucket: string; count: number }>;
  /** Critical path activities (from merged) */
  criticalActivities: Array<{ criticalityBand: string }>;
  criticalCount: number;
  nearCriticalCount: number;
  monitorCount: number;
  /** All milestones (from merged) */
  allMilestones: Array<{ id: string; name: string; varianceDays: number | null }>;
  milestonesWithVariance: Array<{ id: string; name: string; varianceDays: number | null }>;
  hasBaselineData: boolean;
  wbsHotspots: Array<{ wbs_id: string; wbs_short_name: string; wbs_name?: string; hotspotScore: number }>;
  qualityChecks: Array<{ id: string; label: string; value: number; status: string }>;
  /** Typed schedule analytics (s_curve_*, milestone_on_time, resource_demand_capacity) */
  scheduleAnalytics: ScheduleAnalyticsShapes | null;
  trendsData: { hasHistory: boolean; points: TrendsPoint[] };
  /** Activities from project API — used for time-phased planned units charts */
  activities: ScheduleActivity[];
  /** Baseline = target dates; Current = early/CPM dates */
  scheduleDateBasis: ScheduleDateBasisValue;
  /** Include TT_Mile / TT_FinMile (and milestone labels) in time-phased workload */
  includeMilestoneUnits: boolean;
  /** Planned units: working-day (P6 calendar) vs inclusive calendar-day spread */
  unitsSpreadBasis?: UnitsSpreadBasisValue;
}

// ---------------------------------------------------------------------------
// Shared empty state for snapshot-dependent charts (used by hook before calling renderer)
// ---------------------------------------------------------------------------

export const SNAPSHOT_REQUIRED_MESSAGE =
  'At least two schedule imports are required to show this trend. Import another schedule version to begin tracking.';
export const SNAPSHOT_REQUIRED_ACTION_LABEL = 'View Project';

export function snapshotRequiredActionHref(selectedProjectId: string | undefined): string {
  return selectedProjectId ? `/app/projects/${selectedProjectId}` : '/app/projects';
}

// ---------------------------------------------------------------------------
// Chart renderers map
// ---------------------------------------------------------------------------

export const CHART_RENDERERS: Record<string, (props: ChartRenderProps) => React.ReactNode> = {
  float_distribution: (props) => {
    const { floatDist, height, density, showDataLabels, navigate } = props;
    if (floatDist.length === 0) {
      return <ChartEmptyState message="No float distribution data. Import a schedule." actionLabel="View Activities" actionHref="/app/activities" />;
    }
    return (
      <FloatDistributionChart
        data={floatDist}
        height={height}
        showDataLabels={showDataLabels}
        density={density}
        onBucketClick={(bucket) => {
          if (bucket === '<0') navigate(toActivitiesUrl({ negativeFloat: true }));
          else if (bucket === '0') navigate(toActivitiesUrl({ critical: true }));
          else {
            const range = BUCKET_TO_FLOAT_RANGE[bucket];
            if (range) navigate(toActivitiesUrl({ float_min: range.min, float_max: range.max }));
          }
        }}
      />
    );
  },

  criticality_breakdown: (props) => {
    const { criticalActivities, criticalCount, nearCriticalCount, monitorCount, height, density, navigate } = props;
    if (criticalActivities.length === 0) {
      return <ChartEmptyState message="No critical path data. Import a schedule." actionLabel="View Health" actionHref={toAnalyticsUrl('health')} />;
    }
    return (
      <CriticalityChart
        critical={criticalCount}
        nearCritical={nearCriticalCount}
        monitor={monitorCount}
        height={height}
        density={density}
        onSegmentClick={(band) => {
          if (band === 'critical') navigate(toActivitiesUrl({ critical: true }));
          else navigate(toAnalyticsUrl('critical-path'));
        }}
      />
    );
  },

  milestone_variance: (props) => {
    const { allMilestones, hasBaselineData, milestonesWithVariance, height, density, showDataLabels, navigate } = props;
    if (allMilestones.length === 0) {
      return <ChartEmptyState message="No milestone data. Import a schedule with milestones." actionLabel="View Milestones" actionHref={toAnalyticsUrl('milestones')} />;
    }
    if (!hasBaselineData) {
      return (
        <ChartEmptyState
          message="Variance requires target or baseline finish dates per milestone. Ensure activities have target dates set in your schedule, or set a baseline snapshot."
          actionLabel="View Baselines"
          actionHref="/app/baselines"
        />
      );
    }
    const cap = rankedBarTopN(density);
    const msData = cap != null ? getTopN(milestonesWithVariance, cap, 'varianceDays', 'abs') : milestonesWithVariance;
    return (
      <MilestoneVarianceChart data={msData} height={height} showDataLabels={showDataLabels} density={density} onBarClick={() => navigate(toAnalyticsUrl('milestones'))} />
    );
  },

  wbs_hotspots: (props) => {
    const { wbsHotspots, height, density, showDataLabels, navigate } = props;
    if (wbsHotspots.length === 0) {
      return <ChartEmptyState message="No WBS hotspot data. Import a schedule with WBS structure." actionLabel="View Activities" actionHref="/app/activities" />;
    }
    const wbsCap = rankedBarTopN(density);
    const wbsData = wbsCap != null ? getTopN(wbsHotspots, wbsCap, 'hotspotScore', 'desc') : wbsHotspots;
    return (
      <WBSHotspotsChart
        data={wbsData}
        height={height}
        showDataLabels={showDataLabels}
        density={density}
        onItemClick={(wbsId, wbsCode) => navigate(toActivitiesUrl({ wbs_id: wbsId, wbs_code: wbsCode }))}
      />
    );
  },

  quality_issues: (props) => {
    const { qualityChecks, height, density, showDataLabels, navigate } = props;
    if (qualityChecks.length === 0) {
      return <ChartEmptyState message="No quality data. Import a schedule." actionLabel="View Quality" actionHref={toAnalyticsUrl('quality')} />;
    }
    return <QualityChart data={qualityChecks} height={height} showDataLabels={showDataLabels} density={density} onBarClick={() => navigate(toAnalyticsUrl('quality'))} />;
  },

  trends_snapshots: (props) => {
    const { selectedProjectId } = props;
    return (
      <ChartEmptyState
        message={SNAPSHOT_REQUIRED_MESSAGE}
        actionLabel={SNAPSHOT_REQUIRED_ACTION_LABEL}
        actionHref={snapshotRequiredActionHref(selectedProjectId)}
      />
    );
  },

  s_curve_activities: (props) => {
    const { scheduleAnalytics, height, showDataLabels, density, showBaseline, baselineScheduleAnalytics } = props;
    const raw = scheduleAnalytics?.s_curve_activities;
    if (!raw) {
      return <ChartEmptyState message="No data. Import a schedule." actionLabel="View Activities" actionHref="/app/activities" />;
    }
    const baselineRaw = showBaseline ? baselineScheduleAnalytics?.s_curve_activities : undefined;
    return (
      <SCurveActivitiesChart
        planned={raw.planned}
        actual={raw.actual}
        height={height}
        showDataLabels={showDataLabels}
        density={density}
        showBaseline={showBaseline ?? false}
        baselinePlanned={baselineRaw?.planned}
        baselineActual={baselineRaw?.actual}
      />
    );
  },

  s_curve_duration: (props) => {
    const { scheduleAnalytics, height, showDataLabels, density, showBaseline, baselineScheduleAnalytics } = props;
    const raw = scheduleAnalytics?.s_curve_duration;
    if (!raw) {
      return <ChartEmptyState message="No duration data. Import a schedule." actionLabel="View Activities" actionHref="/app/activities" />;
    }
    const baselineRaw = showBaseline ? baselineScheduleAnalytics?.s_curve_duration : undefined;
    return (
      <SCurveDurationChart
        planned={raw.planned}
        earned={raw.earned}
        height={height}
        showDataLabels={showDataLabels}
        density={density}
        showBaseline={showBaseline ?? false}
        baselinePlanned={baselineRaw?.planned}
        baselineEarned={baselineRaw?.earned}
      />
    );
  },

  units_per_day_histogram: (props) => {
    const { activities, scheduleDateBasis, includeMilestoneUnits, height, showDataLabels, density, scheduleAnalytics } =
      props;
    const engineOn = Boolean(scheduleAnalytics?.planned_units_time_phased?.engineEnabled);
    const spreadEff: UnitsSpreadBasis =
      (props.unitsSpreadBasis ?? (engineOn ? 'working' : 'calendar')) as UnitsSpreadBasis;
    if (density === 'preview') {
      const built = resolvePlannedUnitsSeries(
        activities,
        { dateBasis: scheduleDateBasis, includeMilestones: includeMilestoneUnits },
        scheduleAnalytics,
        spreadEff
      );
      if (built.series.length === 0) {
        return (
          <ChartEmptyState
            message="No time-phased planned units. Add planned units and dates on activities."
            actionLabel="View Activities"
            actionHref="/app/activities"
          />
        );
      }
      const bucketed = aggregateAverageUnitsPerDayByCalendarMonth(built.series);
      return (
        <UnitsPerDayHistogramChart
          points={bucketed}
          height={height}
          showDataLabels={false}
          density={density}
        />
      );
    }
    return (
      <PlannedUnitsHistogramPanel
        activities={activities}
        scheduleAnalytics={scheduleAnalytics}
        unitsSpreadBasis={spreadEff}
        scheduleDateBasis={scheduleDateBasis}
        includeMilestoneUnits={includeMilestoneUnits}
        height={height}
        showDataLabels={showDataLabels}
        density={density}
        showDiagnostics={density === 'focus'}
      />
    );
  },

  planned_units_s_curve: (props) => {
    const { activities, scheduleDateBasis, includeMilestoneUnits, height, showDataLabels, density, scheduleAnalytics } =
      props;
    const engineOn = Boolean(scheduleAnalytics?.planned_units_time_phased?.engineEnabled);
    const spreadEff: UnitsSpreadBasis =
      (props.unitsSpreadBasis ?? (engineOn ? 'working' : 'calendar')) as UnitsSpreadBasis;
    const { series, totalPlannedUnits } = resolvePlannedUnitsSeries(
      activities,
      { dateBasis: scheduleDateBasis, includeMilestones: includeMilestoneUnits },
      scheduleAnalytics,
      spreadEff
    );
    if (series.length === 0) {
      return (
        <ChartEmptyState
          message="No time-phased planned units. Add planned units and dates (or enable Include milestone units)."
          actionLabel="View Activities"
          actionHref="/app/activities"
        />
      );
    }
    const previewSeries = density === 'preview' ? sampleCumulativeByCalendarMonth(series) : series;
    const startDate = series[0]?.date ?? '—';
    const finishDate = series[series.length - 1]?.date ?? '—';
    let peakDaily = 0;
    for (const p of series) {
      if (p.unitsPerDay > peakDaily) peakDaily = p.unitsPerDay;
    }
    const basisLine = `${scheduleDateBasis === 'baseline' ? 'Target dates' : 'Early/current'} · ${includeMilestoneUnits ? 'Milestones included' : 'Milestones excluded (default)'}`;

    const chartEl = (
      <PlannedUnitsSCurveChart
        points={previewSeries}
        height={height}
        showDataLabels={showDataLabels}
        density={density}
      />
    );

    if (density !== 'focus') {
      return chartEl;
    }

    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-x-8 gap-y-2 rounded-lg border border-border bg-muted/20 px-4 py-3 text-xs">
          <div>
            <span className="text-muted-foreground block">Total planned units</span>
            <span className="font-medium tabular-nums text-foreground">{totalPlannedUnits.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
          </div>
          <div>
            <span className="text-muted-foreground block">Start date</span>
            <span className="font-medium tabular-nums text-foreground">{startDate}</span>
          </div>
          <div>
            <span className="text-muted-foreground block">Finish date</span>
            <span className="font-medium tabular-nums text-foreground">{finishDate}</span>
          </div>
          <div>
            <span className="text-muted-foreground block">Peak daily load</span>
            <span className="font-medium tabular-nums text-foreground">{peakDaily.toLocaleString(undefined, { maximumFractionDigits: 2 })} units/day</span>
          </div>
          <div className="min-w-[180px]">
            <span className="text-muted-foreground block">Calculation basis</span>
            <span className="font-medium text-foreground">{basisLine}</span>
          </div>
        </div>
        {chartEl}
      </div>
    );
  },

  s_curve_units: (props) => {
    const { scheduleAnalytics, height, showDataLabels, density, showBaseline, baselineScheduleAnalytics } = props;
    const raw = scheduleAnalytics?.s_curve_units;
    const planned = raw?.planned ?? [];
    const earned = raw?.earned ?? [];
    if (!raw || (planned.length === 0 && earned.length === 0)) {
      return (
        <ChartEmptyState
          message="No units data. Import activities with target_work_qty."
          actionLabel="View Activities"
          actionHref="/app/activities"
        />
      );
    }
    const baselineRaw = showBaseline ? baselineScheduleAnalytics?.s_curve_units : undefined;
    return (
      <SCurveDurationChart
        planned={planned}
        earned={earned}
        plannedLabel="Planned units"
        earnedLabel="Earned units"
        height={height}
        showDataLabels={showDataLabels}
        density={density}
        showBaseline={showBaseline ?? false}
        baselinePlanned={baselineRaw?.planned}
        baselineEarned={baselineRaw?.earned}
      />
    );
  },

  s_curve_wbs: (props) => {
    const { scheduleAnalytics, height, showDataLabels, density, showBaseline } = props;
    const raw = scheduleAnalytics?.s_curve_wbs;
    const firstWbs = raw?.series?.[0];
    const points = firstWbs?.points ?? [];
    if (points.length === 0) {
      return <ChartEmptyState message="No WBS progress data. Import a schedule with WBS." actionLabel="View WBS" actionHref="/app/activities" />;
    }
    const xAxisData = points.map((p) => p.date ?? '').filter(Boolean);
    const plannedSeries = points.map((p) => p.planned ?? 0);
    const earnedSeries = points.map((p) => p.earned ?? 0);
    return (
      <SCurveWBSChart
        xAxisData={xAxisData}
        plannedSeries={plannedSeries}
        earnedSeries={earnedSeries}
        height={height}
        showDataLabels={showDataLabels}
        density={density}
        showBaseline={showBaseline ?? false}
      />
    );
  },

  milestone_on_time: (props) => {
    const { scheduleAnalytics, height, showDataLabels, density } = props;
    const raw = scheduleAnalytics?.milestone_on_time;
    const periods = raw?.periods ?? [];
    if (periods.length === 0) {
      return (
        <ChartEmptyState message="No milestones with target dates. Add target dates to milestones." actionLabel="View Milestones" actionHref={toAnalyticsUrl('milestones')} />
      );
    }
    const xAxisData = periods.map((p) => p.period ?? '').filter(Boolean);
    const onTimePct = periods.map((p) => p.on_time_pct ?? 0);
    return (
      <MilestoneOnTimeChart
        xAxisData={xAxisData}
        onTimePct={onTimePct}
        height={height}
        showDataLabels={showDataLabels}
        density={density}
      />
    );
  },

  resource_demand_capacity: (props) => {
    const { scheduleAnalytics, height, showDataLabels, density, maxPoints } = props;
    const raw = scheduleAnalytics?.resource_demand_capacity;
    const buckets = raw?.buckets ?? [];
    const demand = raw?.demand ?? [];
    if (buckets.length === 0 || demand.length === 0) {
      const message =
        raw?.capacity_available === true
          ? 'Resource assignments found but no capacity data available.'
          : 'Resource capacity data is not available. Ensure the schedule includes resource assignments.';
      return (
        <ChartEmptyState
          message={message}
          actionLabel="View Resources"
          actionHref={toAnalyticsUrl('health')}
        />
      );
    }
    let xAxisData = buckets;
    let demandSlice = demand;
    let capacitySlice: number[] | undefined = raw?.capacity_available && Array.isArray(raw.capacity) && raw.capacity.length > 0 ? raw.capacity : undefined;
    if (maxPoints != null && maxPoints > 0) {
      xAxisData = buckets.slice(-maxPoints);
      demandSlice = demand.slice(-maxPoints);
      if (capacitySlice) capacitySlice = capacitySlice.slice(-maxPoints);
    }
    if (density === 'preview' && xAxisData.length > 14) {
      const hasCap = capacitySlice != null && capacitySlice.length === xAxisData.length;
      const toDown: Array<{ name: string; values: number[] }> = [{ name: 'demand', values: demandSlice }];
      if (hasCap && capacitySlice) toDown.push({ name: 'capacity', values: capacitySlice });
      const down = downsampleAlignedSeries(xAxisData, toDown, 14);
      xAxisData = down.xAxisData;
      demandSlice = down.seriesVals[0]!.values;
      capacitySlice = hasCap && down.seriesVals.length > 1 ? down.seriesVals[1]!.values : undefined;
    }
    return (
      <ResourceDemandCapacityChart
        xAxisData={xAxisData}
        demand={demandSlice}
        capacity={capacitySlice}
        height={height}
        showDataLabels={showDataLabels}
        density={density}
      />
    );
  },

  critical_path_length: (props) => {
    const { trendsData, height, showDataLabels, density, maxPoints } = props;
    const points = trendsData.points;
    let xAxisData = points.map((p) => p.uploaded_at ?? '').filter(Boolean);
    let criticalPathDays = points.map((p) => (p.metrics.critical_path_days as number) ?? 0);
    if (maxPoints != null && maxPoints > 0) {
      xAxisData = xAxisData.slice(-maxPoints);
      criticalPathDays = criticalPathDays.slice(-maxPoints);
    }
    return (
      <CriticalPathLengthChart
        xAxisData={xAxisData}
        criticalPathDays={criticalPathDays}
        height={height}
        showDataLabels={showDataLabels}
        density={density}
      />
    );
  },

  float_trend: (props) => {
    const { trendsData, height, showDataLabels, density, maxPoints } = props;
    const points = trendsData.points;
    let xAxisData = points.map((p) => p.uploaded_at ?? '').filter(Boolean);
    let avgFloat = points.map((p) => (p.metrics.avg_float_days as number) ?? 0);
    let minFloat = points.map((p) => (p.metrics.min_float_days as number) ?? 0);
    if (maxPoints != null && maxPoints > 0) {
      xAxisData = xAxisData.slice(-maxPoints);
      avgFloat = avgFloat.slice(-maxPoints);
      minFloat = minFloat.slice(-maxPoints);
    }
    return (
      <FloatTrendChart
        xAxisData={xAxisData}
        avgFloat={avgFloat}
        minFloat={minFloat}
        height={height}
        showDataLabels={showDataLabels}
        density={density}
      />
    );
  },

  near_critical_trend: (props) => {
    const { trendsData, height, showDataLabels, density, maxPoints } = props;
    const points = trendsData.points;
    let xAxisData = points.map((p) => p.uploaded_at ?? '').filter(Boolean);
    let nearCriticalCount = points.map((p) => (p.metrics.near_critical_count as number) ?? 0);
    if (maxPoints != null && maxPoints > 0) {
      xAxisData = xAxisData.slice(-maxPoints);
      nearCriticalCount = nearCriticalCount.slice(-maxPoints);
    }
    return (
      <NearCriticalTrendChart
        xAxisData={xAxisData}
        nearCriticalCount={nearCriticalCount}
        height={height}
        showDataLabels={showDataLabels}
        density={density}
      />
    );
  },

  logic_integrity_trend: (props) => {
    const { trendsData, height, showDataLabels, density, maxPoints } = props;
    const points = trendsData.points;
    let xAxisData = points.map((p) => p.uploaded_at ?? '').filter(Boolean);
    let missingPred = points.map((p) => (p.metrics.missing_pred as number) ?? 0);
    let missingSucc = points.map((p) => (p.metrics.missing_succ as number) ?? 0);
    let constrained = points.map((p) => (p.metrics.constrained_count as number) ?? 0);
    let outOfSequence = points.map((p) => (p.metrics.oos_count as number) ?? 0);
    if (maxPoints != null && maxPoints > 0) {
      xAxisData = xAxisData.slice(-maxPoints);
      missingPred = missingPred.slice(-maxPoints);
      missingSucc = missingSucc.slice(-maxPoints);
      constrained = constrained.slice(-maxPoints);
      outOfSequence = outOfSequence.slice(-maxPoints);
    }
    return (
      <LogicIntegrityTrendChart
        xAxisData={xAxisData}
        missingPred={missingPred}
        missingSucc={missingSucc}
        constrained={constrained}
        outOfSequence={outOfSequence}
        height={height}
        showDataLabels={showDataLabels}
        density={density}
      />
    );
  },

  phase_progress_burnup: (props) => {
    const { trendsData, height, showDataLabels, density, selectedProjectId, maxPoints } = props;
    let points = trendsData.points;
    if (maxPoints != null && maxPoints > 0 && points.length > maxPoints) {
      points = points.slice(-maxPoints);
    }
    if (points.length === 0) {
      return (
        <ChartEmptyState
          message={SNAPSHOT_REQUIRED_MESSAGE}
          actionLabel={SNAPSHOT_REQUIRED_ACTION_LABEL}
          actionHref={snapshotRequiredActionHref(selectedProjectId)}
        />
      );
    }
    return (
      <PhaseProgressBurnupChart
        points={points}
        height={height}
        showDataLabels={showDataLabels}
        density={density}
        emptyStateMessage={SNAPSHOT_REQUIRED_MESSAGE}
        emptyStateActionLabel={SNAPSHOT_REQUIRED_ACTION_LABEL}
        emptyStateActionHref={snapshotRequiredActionHref(selectedProjectId)}
      />
    );
  },
};
